import {SearchBasicDataModel} from "../../../ts/data/search/SearchBasicDataFactory";
import {SearchBasicPageListRouteModel} from "../../../ts/route/search/SearchBasicPageListRouteFactory";
import DataChoice, {IDataChoices} from "../../../ts/model-factory/basic/data/DataChoice";
import RouteChoice from "../../../ts/model-factory/basic/route/RouteChoice";
import {getManager} from "../../../ts/manager/Manager";
import {SearchPageBasicDataModel} from "../../../ts/data/search/SearchPageBasicDataFactory";

interface Props
{
    pageListRouteModel:SearchBasicPageListRouteModel|null
    primaryPageNumber:number;
    returnType?:string
    preventInfiniteScroll?:boolean;
}

export default class SearchViewModel {

    pageListRouteModel:SearchBasicPageListRouteModel|null = null;
    searchPageDataModel:SearchPageBasicDataModel | null = null;
    returnType:string = "";
    primaryPageNumber:number = 1;

    pageModels: SearchBasicDataModel[] = [];
    pageChoice:RouteChoice|null = null;

    preventInfiniteScroll:boolean = false;
    expectsInfiniteScroll:boolean = false;
    usePages:boolean = false;
    showPages :boolean = false;
    showInfiniteScroll:boolean = false;
    numberOfEntries:number = 0

    lastExpectedPageNumber:number = 1;
    lastReceivedPageNumber:number = 0;
    pageChoiceList:RouteChoice[] = [];
    itemChoiceList:DataChoice[] = [];
    itemChoiceOrder:string[] = [];

    initialized : boolean = false;
    initializing : boolean = false;
    receivingData:boolean = false;

    onPageReadyCallback:((completed:boolean) => void)|null = null;

    populate(props: Props)
    {
        this.primaryPageNumber = Math.max(props.primaryPageNumber, 1);
        const returnType = props.returnType ?? "";
        const preventInfiniteScroll = props.preventInfiniteScroll ?? false;
        const searchPageDataModel = props.pageListRouteModel?.sourceDataModel ?? null;
        if((props.pageListRouteModel !== this.pageListRouteModel) || (returnType !== this.returnType) || (preventInfiniteScroll !== this.preventInfiniteScroll) || (searchPageDataModel && !searchPageDataModel.populated && !this.initializing)){

            this.initializing = false;
            this.initialized = false;
            this.pageListRouteModel = props.pageListRouteModel;
            this.returnType = props.returnType ?? "";
            this.preventInfiniteScroll = !!props.preventInfiniteScroll;

            //or page not populated

            this.searchPageDataModel = searchPageDataModel;
        }
    }

    async requireInitialized(): Promise<boolean>
    {

        if(this.initialized || this.initializing){
            return false;
        }
        if(!this.searchPageDataModel){
            this.initialized = true;
            return false;
        }
        this.initializing = true;
        return this.searchPageDataModel.requirePopulated().then(() =>{

            this.initialized = true;
            this.pageChoice = this.pageListRouteModel!.selectedChoice()

            this.expectsInfiniteScroll = false;
            this.usePages = !this.expectsInfiniteScroll;
            this.lastExpectedPageNumber = this.searchPageDataModel!.actualPageCount ?? 1;

            this.pageChoiceList = [];
            this.pageModels.length = 0;
            this.itemChoiceOrder.length = 0;
            this.itemChoiceList.length = 0;

            this.showPages = false;
            this.showInfiniteScroll = false;
            this.numberOfEntries = 0;
            this.lastReceivedPageNumber = 0;

            if(this.expectsInfiniteScroll){
                return this.addNextInfiniteScrollPage(true).then(() => {
                    this.initializing = false;
                    this.initialized = true;
                    return true;
                })
            } else {
                this.initializing = false;
                this.initialized = true;
            }

            return true;
        })
    }

    update()
    {
        if(!this.initialized || !this.searchPageDataModel){
            return;
        }
        this.lastExpectedPageNumber = this.searchPageDataModel!.actualPageCount ?? 1;
        this.pageChoiceList = this.pageListRouteModel!.choiceList();

        this.showPages = this.usePages && (this.pageChoiceList.length > 1);
        this.showInfiniteScroll = this.expectsInfiniteScroll && (this.lastReceivedPageNumber < this.lastExpectedPageNumber);
        this.numberOfEntries = this.searchPageDataModel!.numberOfEntries ?? 0;
        this.updateChoiceList();
        this.checkAddNextInfiniteScrollPage(null);
    }

    async addNextInfiniteScrollPage(initialize:boolean = false): Promise<boolean>
    {
        if(!initialize){
            if(this.receivingData || !this.initialized){
                return false;
            }
        }
        this.receivingData = true;
        let didAddNewPage = false;
        let didAddAny = false;
        this.pageModels.length = 0;
        let lastAddedPageNumber = 0;
        for(let pageNumber = 1; pageNumber <= this.lastExpectedPageNumber; pageNumber++) {
            if(didAddAny && didAddNewPage){
                break;
            }
            const pageModel = this.searchPageDataModel!.getChildModel(pageNumber, this.returnType) || null;
            if(!pageModel){
                continue;
            }
            if(!pageModel.populated){
                await pageModel.requirePopulated();
            }
            const isNewPage = pageNumber > this.lastReceivedPageNumber;
            if(isNewPage){
                didAddNewPage = true;
            }
            this.pageModels.push(pageModel);
            this.lastReceivedPageNumber = Math.max(this.lastReceivedPageNumber, pageNumber);
            lastAddedPageNumber = pageNumber;
            for(let choice of pageModel.choiceList()){
                if(!this.itemChoiceOrder.includes(choice.id)){
                    this.itemChoiceOrder.push(choice.id);
                    didAddAny = true;
                }
            }
        }
        this.lastReceivedPageNumber = lastAddedPageNumber;
        this.receivingData = false;
        return true;
    }

    checkAddNextInfiniteScrollPage(callback:((completed:boolean) => void)|null)
    {
        if(getManager().ui.handlingPageChange){
            if(callback){
                this.onPageReadyCallback = callback;
            }
        } else {
            callback = callback ?? this.onPageReadyCallback;
            if(callback){
                this.onPageReadyCallback = null;
                this.addNextInfiniteScrollPage().then(callback);
            }
        }
    }

    updateChoiceList(){


        this.itemChoiceList.length = 0;
        if(!this.expectsInfiniteScroll){
            const primaryPageModel = this.getPrimaryPageModel()
            if(primaryPageModel){
                this.itemChoiceList.push(... primaryPageModel!.choiceList());
            }
            return;
        }
        const dataChoices:IDataChoices = {};
        for(let pageModel of this.pageModels){
            for(let choice of pageModel.choiceList()){
                dataChoices[choice.id] = choice;
            }
        }
        for(let i = 0; i < this.itemChoiceOrder.length; i++){
            const choiceID = this.itemChoiceOrder[i];
            const choice = dataChoices[choiceID];
            if(!choice){
                this.itemChoiceOrder.splice(i, 1);
                i --;
                continue;
            }
            this.itemChoiceList.push(choice);
        }
    }

    getPrimaryPageModel():SearchBasicDataModel|null
    {
        return this.searchPageDataModel?.getChildModel(this.primaryPageNumber, this.returnType) || null;
    }
}